import { type ComponentType, type LazyExoticComponent, lazy } from 'react';

/**
 * A module exposing exactly the page component we intend to load from it.
 *
 * Naming the export in the type is what lets `lazyRoute` stay cast-free: the
 * compiler checks the string against the module's real exports, so a renamed or
 * deleted page is a type error here rather than a blank screen at runtime.
 */
type PageModule<Name extends string> = { readonly [K in Name]: ComponentType };

/**
 * Wraps a route component in `React.lazy`, resolving a *named* export.
 *
 * `React.lazy` expects a module whose default export is the component; our pages
 * all use named exports, so each loader is adapted rather than the pages being
 * changed. Rendering the result needs a `<Suspense>` boundary above it — the two
 * layouts provide one around their `<Outlet />`.
 */
export const lazyRoute = <Name extends string>(
  name: Name,
  load: () => Promise<PageModule<Name>>,
): LazyExoticComponent<ComponentType> =>
  lazy(async () => {
    const module = await load();
    return { default: module[name] };
  });
